/**
 * Landing page for OAuth provider redirects
 * (/auth/oauth/callback/:provider?code&state).
 *
 * The provider redirect carries a single-use, PKCE-bound state token — the
 * exchange must run exactly once, hence the ref guard (React 18 StrictMode
 * mounts effects twice in dev).
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { getConfig } from '../lib/config';
import { ErrorAlert, Spinner } from '../components/ui';
import { ui, cn } from '../lib/ui';

export default function OAuthCallback() {
  const { provider } = useParams();
  const [searchParams] = useSearchParams();
  const { completeOauthLogin } = useAuth();
  const navigate = useNavigate();
  const { productName } = getConfig();
  const [error, setError] = useState(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const providerError = searchParams.get('error_description') || searchParams.get('error');

    if (providerError) {
      setError(new Error(providerError));
      return;
    }
    if (!code || !state) {
      setError(new Error('The sign-in link is incomplete — please start again.'));
      return;
    }

    completeOauthLogin(provider, { code, state })
      .then(() => navigate('/', { replace: true }))
      .catch((err) => setError(err));
  }, [provider, searchParams, completeOauthLogin, navigate]);

  return (
    <div className={ui.authScreen}>
      <div className={ui.authCard}>
        <div className={ui.authBrand}>
          <h1 className={ui.h1}>{productName}</h1>
          <p className={cn(ui.muted, 'text-body-s')}>Completing sign-in…</p>
        </div>
        {error ? (
          <>
            <ErrorAlert error={error} />
            <p className="text-body-s">
              <Link to="/login">Back to sign in</Link>
            </p>
          </>
        ) : (
          <Spinner label="Signing you in…" />
        )}
      </div>
    </div>
  );
}
